import React from 'react';
import { ChartTooltip } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Shape of the props Recharts injects when this component is used as a
 * `<ComposedChart.Tooltip content>` element. We render two payload
 * entries — one for the revenue bar, one for the markup line — and
 * style each with the indicator that matches its series.
 */
export type RevenueDynamicsTooltipProps = {
    active?: boolean;
    label?: string;
    payload?: ReadonlyArray<{
        color?: string;
        dataKey?: string;
        fill?: string;
        name?: string;
        stroke?: string;
        value?: number | string;
    }>;
    valueFormatter?: (value: number | string) => React.ReactNode;
    /**
     * Re-formats the X-axis label (which is the raw `bucket.date`
     * string the chart receives) into the human form the tooltip
     * header should show — e.g. "10:00" / "5 May" / "May 26".
     * Defaults to passing the raw label through.
     */
    labelFormatter?: (label: string) => React.ReactNode;
};

/**
 * Composed-chart tooltip for the Revenue Dynamics widget. Returns
 * `null` when inactive so recharts keeps the tooltip layer hidden.
 */
export const RevenueDynamicsTooltip: React.FC<RevenueDynamicsTooltipProps> = ({
    active,
    label,
    labelFormatter,
    payload,
    valueFormatter,
}) => {
    if (!active || !payload?.length) {
        return null;
    }

    const headerLabel =
        label !== undefined && labelFormatter
            ? labelFormatter(label)
            : (label ?? '');

    return (
        <ChartTooltip {...getNodeTestIdProp('revenue-dynamics-tooltip')}>
            <ChartTooltip.Header
                {...getNodeTestIdProp('revenue-dynamics-tooltip-header')}
            >
                {headerLabel}
            </ChartTooltip.Header>
            {payload.map((entry, index) => {
                const indicatorColor =
                    entry.color ?? entry.fill ?? entry.stroke;
                return (
                    <ChartTooltip.Item
                        key={`${entry.dataKey ?? ''}-${index}`}
                        {...getNodeTestIdProp(
                            `revenue-dynamics-tooltip-item-${index}`,
                        )}
                    >
                        <ChartTooltip.Indicator
                            color={indicatorColor}
                            {...getNodeTestIdProp(
                                `revenue-dynamics-tooltip-indicator-${index}`,
                            )}
                        />
                        <ChartTooltip.Label
                            {...getNodeTestIdProp(
                                `revenue-dynamics-tooltip-label-${index}`,
                            )}
                        >
                            {entry.name}
                        </ChartTooltip.Label>
                        <ChartTooltip.Value
                            {...getNodeTestIdProp(
                                `revenue-dynamics-tooltip-value-${index}`,
                            )}
                        >
                            {valueFormatter
                                ? valueFormatter(entry.value ?? '')
                                : entry.value}
                        </ChartTooltip.Value>
                    </ChartTooltip.Item>
                );
            })}
        </ChartTooltip>
    );
};
